<script setup lang="ts">
import { scaleLinear, max, type ScaleLinear } from 'd3';
import { useElementBounding } from '@vueuse/core';

// Components
import Grid from './modules/grid.vue';
import YAxis from './modules/yAxis.vue';
import RightYAxis from './modules/rightYAxis.vue';
import Bar from './modules/bar.vue';
import type AreaChart from './modules/area.vue';
import type { TooltipData } from '../tooltip/index.vue';

// Types
export interface BarChartProps {
  data: BarChartData[];
  options: BarChartOptions;
  loading?: boolean;
  clickable?: boolean;
}

export interface BarChartData {
  key: string;
  barValues: { color?: string; value: number }[];
  lineValue?: number;
}

export interface BarChartOptions {
  height?: string;
  unit?: string;
  y?: {
    unit?: string;
    grace?: number;
  };
  y2?: {
    unit?: string;
    grace?: number;
  };
  tooltip?: (dataIndex: number) => TooltipData | undefined;
}

// Props
const emit = defineEmits<{
  (e: 'click-on-bar', index: number): void;
}>();
const props = withDefaults(defineProps<BarChartProps>(), {
  options: () => ({
    height: 'h-[50vh]',
    y: {
      unit: undefined,
      grace: 0.3,
    },
    y2: {
      unit: undefined,
      grace: 0.3,
    },
  }),
  loading: false,
  clickable: false,
});

// Refs
const yAxisRef = ref<InstanceType<typeof YAxis> | null>(null);
const rightYAxisRef = ref<InstanceType<typeof RightYAxis> | null>(null);
const areaChartRef = ref<InstanceType<typeof AreaChart> | null>(null);
const yAxisValues = computed(() => yAxisRef.value?.yAxisValues ?? []);
const rightYAxisValues = computed(() => rightYAxisRef.value?.rightYAxisValues ?? []);
const bottomMargin = ref(0);
const rightMargin = ref(25);

// ================================================ Scales
const calculateScale = (values: number[], graceFactor = 0.1) => {
  const maxValue = max(values) ?? 0;
  const minValue = Math.min(0, ...values);

  if (maxValue <= 0 && minValue >= 0) return scaleLinear().domain([0, 10]).range([100, 0]);

  // Calcul avec le grace
  const targetMax = maxValue * (1 + graceFactor);
  const targetMin = minValue < 0 ? minValue * (1 + graceFactor) : minValue;

  // Trouver l'ordre de grandeur et un diviseur approprié
  const maxMagnitude = Math.floor(Math.log10(Math.abs(targetMax || 1)));
  const minMagnitude = minValue < 0 ? Math.floor(Math.log10(Math.abs(targetMin))) : 0;
  const magnitude = Math.max(maxMagnitude, minMagnitude);
  const divisor = Math.pow(10, magnitude > 0 ? magnitude - 1 : magnitude);

  // Arrondir à un nombre "propre" juste supérieur à targetMax et inférieur à targetMin
  const roundedMax = Math.ceil(targetMax / divisor) * divisor;
  const roundedMin = minValue < 0 ? Math.floor(targetMin / divisor) * divisor : 0;

  return scaleLinear().domain([roundedMin, roundedMax]).range([100, 0]);
};

// Generate consistent ticks for both axes
const generateTicks = (scale: ScaleLinear<number, number>, numTicks = 10) => {
  const domain = scale.domain();
  const min = domain[0] || 0;
  const max = domain[1] || 0;
  if (min === max) return [0];

  const range = max - min;
  const step = range / (numTicks - 1);

  return Array.from({ length: numTicks }, (_, i) => min + i * step);
};

const yScale = computed(() => {
  const grace = props.options.y?.grace ?? 0.1;
  return calculateScale(
    props.data.flatMap((d) => d.barValues.map((b) => b.value)),
    grace,
  );
});

const y2Scale = computed(() => {
  const grace = props.options.y2?.grace ?? 0.1;
  return calculateScale(
    props.data.map((d) => d.lineValue ?? 0),
    grace,
  );
});

// ================================================ Computed
const chartStyle = computed(() => ({
  '--marginTop': '16px',
  '--marginRight': `${rightMargin.value}px`,
  '--marginBottom': `${bottomMargin.value}px`,
  '--marginLeft': `${leftMargin.value}px`,
}));

// Shared number of ticks for consistent grid lines between axes
const numTicks = 10;

// Generate consistent tick values for both axes
const yTicks = computed(() => generateTicks(yScale.value, numTicks));
const y2Ticks = computed(() => generateTicks(y2Scale.value, numTicks));

const leftMargin = computed(() => {
  if (!yAxisValues.value.length) return 0;
  const barValues = yAxisValues.value.map((value) => {
    const { width } = useElementBounding(value);
    return width.value;
  });
  return Math.max(...barValues);
});

// ================================================ Methods
const updateBottomMargin = (value: HTMLElement[]) => {
  if (!value.length) return 0;
  const values = value.map((value) => {
    const { height } = useElementBounding(value);
    return height.value;
  });
  bottomMargin.value = Math.max(...values) + 8;
};

watch(
  rightYAxisValues,
  () => {
    if (!rightYAxisValues.value.length || !props.options.y2) {
      rightMargin.value = 16;
      return;
    }

    const values = rightYAxisValues.value.map((value) => {
      const { width } = useElementBounding(value);
      return width.value;
    });
    rightMargin.value = Math.max(...values) + 8;
  },
  { deep: true },
);

const handleHoverPoint = (index: number) => {
  if (areaChartRef.value) {
    areaChartRef.value.setActivePoint(index);
  }
};

const handleUnhoverPoint = () => {
  if (areaChartRef.value) {
    areaChartRef.value.setActivePoint(-1);
  }
};

const handleClick = (index: number) => {
  emit('click-on-bar', index);
};
</script>

<template>
  <div class="relative w-full grid mt-0 mr-[25px]" :class="props.options.height" :style="chartStyle">
    <y-axis
      ref="yAxisRef"
      :loading="props.loading"
      :y-scale="yScale"
      :options="props.options"
      :num-ticks="numTicks"
      :custom-ticks="yTicks"
    />
    <right-y-axis
      v-if="props.data.some((d) => d.lineValue)"
      ref="rightYAxisRef"
      :loading="props.loading"
      :y-scale="y2Scale"
      :options="props.options"
      :num-ticks="numTicks"
      :custom-ticks="y2Ticks"
    />
    <div
      class="absolute inset-0 h-[calc(100%-var(--marginTop)-var(--marginBottom))] w-[calc(100%-var(--marginLeft)-var(--marginRight))] translate-x-[var(--marginLeft)] translate-y-[var(--marginTop)] overflow-visible"
    >
      <div class="relative w-full h-full">
        <grid :loading="props.loading" :y-scale="yScale" :num-ticks="numTicks" :custom-ticks="yTicks" />
        <area-chart
          v-if="props.data.some((d) => d.lineValue)"
          ref="areaChartRef"
          :data="props.data"
          :loading="props.loading"
          :y-scale="y2Scale"
        />
        <bar
          :data="props.data"
          :loading="props.loading"
          :y-scale="yScale"
          :tooltip="props.options.tooltip"
          :clickable="clickable"
          @x-axis-values="updateBottomMargin"
          @hover-point="handleHoverPoint"
          @unhover-point="handleUnhoverPoint"
          @click-point="handleClick"
        />
      </div>
    </div>
  </div>
</template>
